import { useCallback, useEffect, useMemo } from 'react';
import { useBuilder } from '~/app/lib/redux/selectors/builder-selector';
import { useDynamicGrid } from '~/app/lib/redux/selectors/dynamic-grid-selector';
import { getInitialState } from '../../dynamic-grid-helpers';

// Helper function to create a safe object without prototype
const createSafeObject = () => Object.create(null);

// Helper function to validate object keys
const isValidKey = (key: string): boolean => {
  const dangerousKeys = [
    '__proto__',
    'constructor',
    'prototype',
    'hasOwnProperty',
    'toString',
    'valueOf',
  ];
  return !dangerousKeys.includes(key) && typeof key === 'string';
};

export const usePopulateValues = ({
  fieldPath,
  setState,
  fields,
}: {
  fieldPath: string;
  setState: (state: any) => void;
  fields: { fieldKey: string; initialValue: any }[];
}) => {
  const { flatOutline, selected: selectedComponentId } = useBuilder();
  const { componentPath } = useDynamicGrid();
  const currentComponent = flatOutline?.[selectedComponentId];

  const getFieldName = useCallback(
    (field: string) => {
      if (fieldPath) {
        return `${fieldPath}.${field}`;
      }
      return field;
    },
    [fieldPath]
  );

  const initialState = useMemo(() => {
    return fields.reduce((acc: any, field) => {
      const keys = field.fieldKey.split('.');
      let current = acc;

      // Traverse nested object structure for fields like 'mobile.fontSize' and build intermediate objects
      keys.slice(0, -1).forEach((key) => {
        if (!isValidKey(key)) {
          console.warn(`Invalid key detected: ${key}`);
          return;
        }

        // Create empty object for this level if it doesn't exist
        if (!current[key]) {
          current[key] = createSafeObject();
        }
        // Move to next level
        current = current[key];
      });

      // Get the final property name
      const propertyName = keys[keys.length - 1];

      if (!isValidKey(propertyName)) {
        console.warn(`Invalid property name detected: ${propertyName}`);
        return acc;
      }

      // Set the value, getting initial state from component or using default
      current[propertyName] = getInitialState(
        currentComponent,
        componentPath,
        getFieldName(field.fieldKey),
        field.initialValue
      );

      return acc;
    }, createSafeObject());
  }, [currentComponent, componentPath, fields, getFieldName]);

  useEffect(() => {
    setState(initialState);
  }, [initialState, setState]);
};
